import type { LabSourceEngine } from '../types';

export type SourceFile = { file: File; path: string };

export type DetectedSource = {
  engine: LabSourceEngine;
  projectRoot: string;
  files: SourceFile[];
};

function parentPath(filePath: string): string {
  const parts = filePath.split('/');
  parts.pop();
  return parts.join('/') || '.';
}

export function detectSourceProject(files: SourceFile[]): DetectedSource {
  const godotRoots = files
    .filter(({ path }) => path.toLowerCase().endsWith('/project.godot') || path.toLowerCase() === 'project.godot')
    .map(({ path }) => parentPath(path));
  const unrealRoots = files
    .filter(({ path }) => path.toLowerCase().endsWith('.uproject'))
    .map(({ path }) => parentPath(path));
  const unityMarkers = ['assets', 'packages', 'projectsettings'];
  const unityMarkerSets = new Map<string, Set<string>>();
  for (const { path } of files) {
    const segments = path.split('/');
    for (let index = 0; index < segments.length; index += 1) {
      const marker = segments[index].toLowerCase();
      if (!unityMarkers.includes(marker)) continue;
      const root = segments.slice(0, index).join('/') || '.';
      const found = unityMarkerSets.get(root) ?? new Set<string>();
      found.add(marker);
      unityMarkerSets.set(root, found);
      break;
    }
  }
  const unityRoots = [...unityMarkerSets.entries()]
    .filter(([, found]) => unityMarkers.every((marker) => found.has(marker)))
    .map(([root]) => root);

  const matches: Array<{ engine: LabSourceEngine; root: string }> = [];
  for (const root of godotRoots) matches.push({ engine: 'godot', root });
  for (const root of unrealRoots) matches.push({ engine: 'unreal', root });
  if (unityRoots.length === 1) matches.push({ engine: 'unity', root: unityRoots[0] });
  if (matches.length !== 1) {
    throw new Error(matches.length === 0
      ? 'Choose a Godot, Unity, or Unreal project folder.'
      : 'This folder contains more than one game project. Choose one project root.');
  }

  const match = matches[0];
  const prefix = match.root === '.' ? '' : `${match.root}/`;
  return {
    engine: match.engine,
    projectRoot: match.root,
    files: files.filter(({ path }) => path === match.root || path.startsWith(prefix)),
  };
}
